import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { getGuestBasket } from '../lib/guestBasket';
/**
 * App shell — phone-app feel on mobile (480px max-width centered, internal
 * scroll), full responsive layout on desktop (≥768px).
 */
export function Layout() {
  const { user, profile, signOut } = useAuth();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bagCount, setBagCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // Load the admin-uploaded nav logo (if any) once on mount. Falls back to
  // the wordmark when nothing's published.
  useEffect(() => {
    apiFetch<Record<string, { image_url?: string }>>('/api/catalog/site-content')
      .then((all) => {
        const url = all.nav_logo?.image_url;
        if (url) setLogoUrl(url);
      })
      .catch(() => {
        // ignore — wordmark fallback renders
      });
  }, []);
  // Refresh bag count whenever the user logs in/out, the route changes, or
  // a page dispatches the `lr:basket-changed` event after add/remove.
  // For guests the count comes from localStorage (no API call needed);
  // signed-in users pull from the server basket.
  useEffect(() => {
    let cancelled = false;
    function refresh() {
      if (!user) {
        setBagCount(getGuestBasket().length);
        return;
      }
      apiFetch<{ items: unknown[] }>('/api/basket')
        .then((b) => {
          if (!cancelled) setBagCount(b.items?.length ?? 0);
        })
        .catch(() => {
          if (!cancelled) setBagCount(0);
        });
    }
    refresh();
    const handler = () => refresh();
    window.addEventListener('lr:basket-changed', handler);
    // Also sync if a different tab edited the guest basket.
    const storageHandler = (e: StorageEvent) => {
      if (e.key === 'lr-guest-basket') handler();
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      cancelled = true;
      window.removeEventListener('lr:basket-changed', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, [user, location.pathname]);
  // Routes that hide the bottom tab bar (checkout / auth / payment flows).
  const hideTabbar = /^\/(sign-in|sign-up|checkout|credits\/payment|credits\/success)/.test(
    location.pathname
  );
  // Close the side menu on route change so it never stays stuck open.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  // Reset any pinch-zoom the user did on the previous page. iOS Safari
  // ignores `user-scalable=no`, so we briefly toggle the viewport meta tag
  // to force the browser back to a 1x scale on every navigation.
  useEffect(() => {
    const vp = document.querySelector('meta[name=viewport]');
    if (!vp) return;
    const original = vp.getAttribute('content') ?? '';
    vp.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
    );
    // Force re-apply so the scale clamp actually fires even if the value
    // hasn't changed — a no-op re-set works in iOS Safari.
    const tmp = vp.getAttribute('content');
    vp.setAttribute('content', original || tmp || '');
  }, [location.pathname]);
  const goSignIn = () => navigate('/sign-in');
  const goSignUp = () => navigate('/sign-up');
  // Guests don't have a server basket / credits yet — the bag icon should
  // take them to /credits where they can sign up during purchase. Signed-
  // in customers go straight to checkout as normal.
  const bagDestination = '/checkout';
  return (
    <div className={`app${hideTabbar ? ' no-tabbar' : ''}`}>
      <div className="statusbar" />
      <header className="header">
        <button
          className="icon-btn menu-btn-mobile"
          onClick={() => setMenuOpen(true)}
          aria-label="Menu"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          >
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <Link to="/" className="brand" aria-label="Label Rotation">
          {logoUrl ? (
            <img className="brand-logo" src={logoUrl} alt="Label Rotation" />
          ) : (
            <span className="brand-wordmark">LABEL ROTATION</span>
          )}
        </Link>
        {/* Desktop nav */}
        <nav className="desktop-nav">
          <DNavLink to="/">HOME</DNavLink>
          <DNavLink to="/browse">BROWSE</DNavLink>
          <DNavLink to="/how-it-works">HOW IT WORKS</DNavLink>
          {user && <DNavLink to="/account">MY ROTATION</DNavLink>}
          <DNavLink to="/credits">ADD CREDITS</DNavLink>
          <DNavLink to="/contact">CONTACT</DNavLink>
          <div className="dnav-auth">
            {user ? (
              <>
                {profile && (
                  <Link to="/credits" className="dnav-credits" title="Add more credits">
                    <span className="val">{profile.credit_balance ?? 0}</span>
                    <span className="lbl">
                      {profile.credit_balance === 1 ? 'CREDIT' : 'CREDITS'}
                    </span>
                  </Link>
                )}
                {isAdmin && <DNavLink to="/admin">ADMIN</DNavLink>}
                <button className="dnav-link" onClick={() => void signOut()}>
                  SIGN OUT
                </button>
              </>
            ) : (
              <>
                <button className="dnav-link" onClick={goSignIn}>
                  SIGN IN
                </button>
                <button className="dnav-link dnav-cta" onClick={goSignUp}>
                  JOIN
                </button>
              </>
            )}
          </div>
        </nav>
        <Link
          to={bagDestination}
          className="icon-btn bag"
          aria-label={`Bag (${bagCount} ${bagCount === 1 ? 'item' : 'items'})`}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 7h12l-1 13H7L6 7z" />
            <path d="M9 7a3 3 0 016 0" />
          </svg>
          {bagCount > 0 && <span className="count">{bagCount}</span>}
        </Link>
      </header>
      {/* ID verification reminder — surfaces to logged-in customers who haven't
          submitted ID yet. Hidden on the verify-id page itself so we don't
          double-nudge. */}
       {/* ID banner hidden — remove the surrounding comment to re-enable
      {user &&
        profile &&
        profile.id_status !== 'approved' &&
        !location.pathname.startsWith('/verify-id') &&
        !location.pathname.startsWith('/admin') && <IdBanner status={profile.id_status} />}
      */}
      <Outlet />
      {/* Small site footer — desktop only. On mobile the tabbar + side menu
          provide all the same links. */}
      <SiteFooter bagDestination={bagDestination} />
      {/* Bottom tab bar removed — the header bag icon + hamburger side menu
          cover all the same routes without crowding the bottom of the screen. */}
      {/* Mobile side menu */}
      <div
        className={`menu-overlay${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={`side-menu${menuOpen ? ' open' : ''}`}>
        <button className="close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="menu-brand">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Label Rotation"
              style={{ height: 72, width: 'auto', maxWidth: 260, objectFit: 'contain' }}
            />
          ) : (
            'LABEL ROTATION'
          )}
        </div>
        {user && profile && (
          <>
            <div className="menu-user">
              <div className="name">
                {profile.first_name ?? profile.email?.split('@')[0] ?? 'You'}
              </div>
              <div className="email">{profile.email}</div>
            </div>
            <Link to="/credits" className="menu-credits">
              <div>
                <div className="lbl">YOUR CREDITS</div>
                <div className="val">
                  {profile.credit_balance ?? 0}{' '}
                  {profile.credit_balance === 1 ? 'CREDIT' : 'CREDITS'}
                </div>
              </div>
              <span className="add">+ ADD</span>
            </Link>
          </>
        )}
        <Link to="/" className="menu-item">
          HOME
          <Chev />
        </Link>
        <Link to="/browse" className="menu-item">
          BROWSE
          <Chev />
        </Link>
        <Link to="/how-it-works" className="menu-item">
          HOW IT WORKS
          <Chev />
        </Link>
        {user && (
          <Link to="/account" className="menu-item">
            MY ROTATION
            <Chev />
          </Link>
        )}
        <Link to="/credits" className="menu-item">
          ADD CREDITS
          <Chev />
        </Link>
        <Link to="/contact" className="menu-item">
          CONTACT
          <Chev />
        </Link>
        {isAdmin && (
          <Link to="/admin" className="menu-item">
            ADMIN
            <Chev />
          </Link>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 16 }}>
          {user ? (
            <button
              className="menu-item"
              style={{ borderBottom: 'none', color: 'var(--ink-soft)' }}
              onClick={() => void signOut()}
            >
              SIGN OUT
              <Chev />
            </button>
          ) : (
            <>
              <Link
                to="/sign-in"
                className="menu-item"
                style={{ borderBottom: 'none' }}
              >
                SIGN IN
                <Chev />
              </Link>
              <Link
                to="/sign-up"
                style={{
                  display: 'block',
                  marginTop: 14,
                  background: 'var(--ink)',
                  color: '#fff',
                  borderRadius: 'var(--radius-pill)',
                  padding: '14px 22px',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textAlign: 'center'
                }}
              >
                JOIN THE ROTATION
              </Link>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
function DNavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `dnav-link${isActive ? ' active' : ''}`}
    >
      {children}
    </NavLink>
  );
}
function TabLink({
  to,
  label,
  children
}: {
  to: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => `tab${isActive ? ' active' : ''}`}>
      {children}
      <span>{label}</span>
    </NavLink>
  );
}
function Chev() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
function SiteFooter({ bagDestination }: { bagDestination: string }) {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="site-footer-cols">
          <div>
            <div className="brand-wordmark" style={{ marginBottom: 6 }}>
              LABEL ROTATION
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              Your wardrobe on rotation.
            </div>
          </div>
          <div>
            <div className="site-footer-h">SHOP</div>
            <Link to="/browse">Browse</Link>
            <Link to="/credits">Credits</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/faq">FAQ</Link>
          </div>
          <div>
            <div className="site-footer-h">ACCOUNT</div>
            <Link to="/account">My rotation</Link>
            <Link to={bagDestination}>Bag</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div>
            <div className="site-footer-h">LEGAL</div>
            <Link to="/terms">Terms &amp; Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/returns">Returns &amp; Rental</Link>
            <Link to="/damage-loss">Damage &amp; Loss</Link>
            <Link to="/cookies">Cookies</Link>
          </div>
        </div>
        <div className="site-footer-base">
          <span>© {year} Label Rotation</span>
          <a href="mailto:team@labelrotation.co.uk">team@labelrotation.co.uk</a>
        </div>
      </div>
    </footer>
  );
}
function IdBanner({ status }: { status: string }) {
  const copy =
    status === 'pending'
      ? 'Your ID is being reviewed — usually within 24 hours.'
      : status === 'rejected'
        ? 'Your ID was rejected — please upload a new one.'
        : 'Please add your ID to verify your account before your first order ships.';
  const action = status === 'pending' ? 'View status' : 'Add ID';
  return (
    <Link
      to="/verify-id"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        background:
          status === 'pending'
            ? 'var(--card)'
            : status === 'rejected'
              ? '#f5dad6'
              : 'var(--ink)',
        color:
          status === 'pending'
            ? 'var(--ink)'
            : status === 'rejected'
              ? 'var(--danger)'
              : '#fff',
        padding: '10px 16px',
        fontSize: 12,
        fontWeight: 600,
        textDecoration: 'none',
        borderBottom: '1px solid var(--line-soft)',
        flexShrink: 0
      }}
    >
      <span>{copy}</span>
      <span
        style={{
          fontWeight: 700,
          letterSpacing: '0.14em',
          fontSize: 11,
          whiteSpace: 'nowrap'
        }}
      >
        {action} →
      </span>
    </Link>
  );
}
